import type {
  FormFieldDescriptor,
  FormOptionDescriptor,
  FormOptionSourceDescriptor,
  ResolvedFormOption,
} from '@finance/contracts';
import { getOptionAdapter } from './option-adapter-registry';
import { resolveTKey } from '../../i18n/resolve-key';
import type { StaticTranslator } from '../../i18n/static-translator';

export interface OptionResolverContext {
  resolveOptions: (
    source: FormOptionSourceDescriptor,
    values: Record<string, unknown>,
    options?: { query?: Record<string, unknown> },
  ) => Promise<FormOptionDescriptor[]>;
}

function evaluateValueExpression(
  expr: import('@finance/contracts').ValueExpression,
  values: Record<string, unknown>,
): unknown {
  if (expr.fieldId !== undefined) return values[expr.fieldId as string];
  if (expr.constant !== undefined) return expr.constant;
  return undefined;
}

export async function resolveFieldOptions(
  field: FormFieldDescriptor,
  values: Record<string, unknown>,
  ctx?: OptionResolverContext,
): Promise<FormOptionDescriptor[]> {
  if (field.options) return field.options;

  if (field.optionSource) {
    const source = field.optionSource;
    const query: Record<string, unknown> = {};
    if (source.queryParams) {
      for (const [key, expr] of Object.entries(source.queryParams)) {
        query[key] = evaluateValueExpression(expr, values);
      }
    }

    if (ctx) return ctx.resolveOptions(source, values, { query });

    const adapter = getOptionAdapter(source.adapterId as string);
    if (adapter) return adapter(source, { values, query });

    throw new Error(`Unknown option adapter: ${String(source.adapterId)}`);
  }

  return [];
}

export function resolveOptionLabel(opt: FormOptionDescriptor, t: StaticTranslator): string {
  if (opt.label !== undefined && opt.label !== null && opt.label !== '') return opt.label;
  if (opt.labelKey) return t(resolveTKey(opt.labelKey));
  return String(opt.value);
}

export function toResolvedOptions(
  options: FormOptionDescriptor[],
  t: StaticTranslator,
): ResolvedFormOption[] {
  return options.map((opt) => ({
    value: opt.value,
    label: resolveOptionLabel(opt, t),
    description: opt.descriptionKey ? t(resolveTKey(opt.descriptionKey)) : undefined,
    disabled: opt.disabled,
    metadata: opt.metadata,
  }));
}

export function getOptionDependencies(field: FormFieldDescriptor): string[] {
  if (!field.optionSource?.queryDependencies) return [];
  return field.optionSource.queryDependencies as string[];
}

export function isSearchable(field: FormFieldDescriptor): boolean {
  return !!field.optionSource?.allowSearch;
}

export function getMinSearchLength(field: FormFieldDescriptor): number {
  return field.optionSource?.minimumSearchLength ?? 0;
}
